import { useEffect, useState, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { AlertTriangle, X } from "lucide-react";
import SideMenu from "../components/Menus/SideMenu";
import UpperMenu from "../components/Menus/UpperMenu";
import Models from "../components/Models";
import Pricing from "../components/Pricing";
import RateLimits from "../components/RateLimits";
import BillingDashboard from "../components/Dashboard";
import APIKeys from "../components/APIKeys";
import Recharge from "../components/Recharge";
import Receipts from "../components/Receipts";
import Info from "../components/Info";
import BatchAPI from "../components/BatchApi";
import Files from "../components/Files";
import Organizations from "../components/Organizations";
import PlaygroundPage from "../components/Playground/PlaygroundPage";
import { TitleContext } from "../contexts/TitleContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { API_BASE } from "../shared/Constants";

// Bump this key whenever a new deprecation announcement should re-show the
// banner to users who previously dismissed it. The current value reflects
// the 2026-07-15 sunset of sabia-3, sabia-3.1, and sabiazinho-3.
const DEPRECATION_BANNER_KEY = "maritalk-deprecation-banner-2026-07-15-dismissed";

function Home() {
  const { isAuthenticated, isLoading } = useAuth0();
  const { title } = useContext(TitleContext);
  const { isDarkMode } = useContext(ThemeContext);
  const [maintenanceBanner, setMaintenanceBanner] = useState(false);
  const [maintenanceEta, setMaintenanceEta] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [deprecationBannerVisible, setDeprecationBannerVisible] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/maintenance-status`)
      .then((res) => res.json())
      .then((data) => {
        setMaintenanceBanner(data.maintenance_mode);
        setMaintenanceEta(data.maintenance_eta);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(DEPRECATION_BANNER_KEY);
      if (!dismissed) setDeprecationBannerVisible(true);
    } catch {
      // localStorage may be unavailable (private mode, blocked storage) — fall
      // back to showing the banner each visit rather than hiding it forever.
      setDeprecationBannerVisible(true);
    }
  }, []);

  const dismissDeprecationBanner = () => {
    setDeprecationBannerVisible(false);
    try {
      window.localStorage.setItem(DEPRECATION_BANNER_KEY, "1");
    } catch {
      // Storage write failed; banner will reappear next visit. Acceptable.
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCode = params.has("code");
    const hasError = params.has("error");
    if (!isLoading && !isAuthenticated) {
      if (hasError) {
        const search = params.toString();
        window.location.href = `/auth${search ? `?${search}` : ""}`;
        return;
      }
      if (!hasCode) window.location.href = "/auth";
    }
  }, [isLoading, isAuthenticated]);

  // Reflect ThemeContext as a `dark` class on <html> so Tailwind dark variants work
  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [isDarkMode]);

  if (isLoading || !isAuthenticated) return null;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
      <SideMenu />
      <main className="flex flex-1 flex-col overflow-hidden">
        <UpperMenu title={title} />
        {deprecationBannerVisible && (
          <div
            data-testid="deprecation-banner"
            className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-6 py-3 md:px-8"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
            <p className="flex-1 text-sm text-foreground/90">
              <strong className="text-foreground">Descontinuação anunciada:</strong> a partir de{" "}
              <strong className="text-foreground">15/07/2026</strong>, os modelos{" "}
              <code className="rounded border border-border bg-muted px-1 font-mono text-[12px]">sabia-3</code>,{" "}
              <code className="rounded border border-border bg-muted px-1 font-mono text-[12px]">sabia-3.1</code> e{" "}
              <code className="rounded border border-border bg-muted px-1 font-mono text-[12px]">sabiazinho-3</code>{" "}
              deixarão de estar disponíveis. Migre para{" "}
              <code className="rounded border border-border bg-muted px-1 font-mono text-[12px]">sabia-4</code> ou{" "}
              <code className="rounded border border-border bg-muted px-1 font-mono text-[12px]">sabiazinho-4</code>.
            </p>
            <button
              onClick={dismissDeprecationBanner}
              aria-label="Fechar aviso de descontinuação"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-foreground/60 transition-colors hover:bg-warning/20 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {maintenanceBanner && !bannerDismissed && (
          <div className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-6 py-3 md:px-8">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
            <p className="flex-1 text-sm text-foreground/90">
              <strong className="text-foreground">Estamos em manutenção.</strong> Pagamentos e
              algumas operações estão temporariamente desabilitados.{" "}
              <strong className="text-foreground">Todo o uso via API está gratuito</strong> até o
              término da manutenção.
              {maintenanceEta && (
                <>
                  {" "}
                  Previsão de término: <strong className="text-foreground">{maintenanceEta}</strong>.
                </>
              )}
            </p>
            <button
              onClick={() => setBannerDismissed(true)}
              aria-label="Fechar aviso"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-foreground/60 transition-colors hover:bg-warning/20 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto bg-surface">
          <div className="w-full px-6 py-6 md:px-8">
            {title === "Plataforma" && <Info />}
            {title === "Modelos" && <Models />}
            {title === "Preços" && <Pricing />}
            {title === "Rate Limits" && <RateLimits />}
            {title === "Gastos" && <BillingDashboard />}
            {title === "Chaves de API" && <APIKeys />}
            {title === "Recarga" && <Recharge />}
            {title === "Comprovantes" && <Receipts />}
            {title === "Batch API" && <BatchAPI />}
            {title === "Arquivos" && <Files />}
            {title === "Organizações" && <Organizations />}
            {title === "Playground" && <PlaygroundPage />}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Home;
